import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Eye, EyeOff, User, Mail, Shield, CheckCircle } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, token } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password Changed', {
        description: 'Your password has been updated successfully.'
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
      toast.error('Error', {
        description: err.response?.data?.detail || 'Failed to change password'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-4xl" data-testid="settings-page">
        <div>
          <h1 className="text-4xl font-bold text-carbon-black mb-2" data-testid="settings-title">Settings</h1>
          <p className="text-slate-gray">Manage your account settings and preferences</p>
        </div>

        {/* Profile Info Card */}
        <Card className="shadow-lg rounded-xl border-0">
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Profile Information
            </CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-gray mb-1">First Name</label>
                <div className="flex items-center p-3 bg-cloud-white rounded-lg border border-cool-gray">
                  <User className="h-5 w-5 text-slate-gray mr-2" />
                  <span className="text-carbon-black font-medium">{user?.first_name}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-gray mb-1">Last Name</label>
                <div className="flex items-center p-3 bg-cloud-white rounded-lg border border-cool-gray">
                  <User className="h-5 w-5 text-slate-gray mr-2" />
                  <span className="text-carbon-black font-medium">{user?.last_name}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-gray mb-1">Email Address</label>
                <div className="flex items-center p-3 bg-cloud-white rounded-lg border border-cool-gray">
                  <Mail className="h-5 w-5 text-slate-gray mr-2" />
                  <span className="text-carbon-black font-medium">{user?.email}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-gray mb-1">Role</label>
                <div className="flex items-center p-3 bg-cloud-white rounded-lg border border-cool-gray">
                  <Shield className="h-5 w-5 text-slate-gray mr-2" />
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    user?.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user?.role === 'admin' ? '👑 Admin' : '👤 User'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card className="shadow-lg rounded-xl border-0">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Lock className="h-5 w-5 mr-2" />
              Change Password
            </CardTitle>
            <CardDescription>Update your password to keep your account secure</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-6" data-testid="change-password-form">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm" data-testid="change-password-error">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm flex items-center" data-testid="change-password-success">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Password changed successfully!
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-2">Current Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-gray" />
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                    placeholder="Enter current password"
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-gray hover:text-carbon-black"
                  >
                    {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-2">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-gray" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                    placeholder="Enter new password"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-gray hover:text-carbon-black"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-gray mt-1">Must be at least 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-2">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-gray" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                    placeholder="Confirm new password"
                    required
                    data-testid="confirm-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-gray hover:text-carbon-black"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="gradient-fearless text-white"
                disabled={loading}
                data-testid="change-password-btn"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
